/**
 * Peptide Healing Intake Template
 * 
 * BPC-157, TB-500, and other healing/recovery peptides
 * Focused on injury assessment and athlete screening
 */

import { IntakeTemplate, IntakeSection, IntakeQuestion } from './types';

// ============================================================================
// Injury/Condition Assessment
// ============================================================================

const INJURY_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'primary_condition',
    text: 'What is the primary condition you want to address?',
    inputType: 'select',
    options: [
      'Muscle injury/strain',
      'Tendon injury (tendinitis/tendinopathy)',
      'Ligament injury',
      'Joint pain/inflammation',
      'Post-surgical recovery',
      'Chronic pain',
      'Sports injury',
      'Gut health/digestive issues',
      'General recovery/wellness',
      'Other'
    ],
    required: true
  },
  {
    id: 'condition_details',
    text: 'Please describe the condition in more detail:',
    inputType: 'text',
    required: true,
    helpText: 'Include location, symptoms, and how it affects you'
  },
  {
    id: 'injury_location',
    text: 'Where is the injury/condition located?',
    inputType: 'multiselect',
    options: [
      'Head/Neck',
      'Shoulder',
      'Elbow/Forearm',
      'Wrist/Hand',
      'Upper Back',
      'Lower Back',
      'Hip',
      'Knee',
      'Ankle/Foot',
      'Abdomen/GI',
      'Multiple locations',
      'Systemic/Whole body'
    ],
    required: true
  },
  {
    id: 'injury_duration',
    text: 'How long have you been experiencing this condition?',
    inputType: 'select',
    options: [
      'Less than 1 week (acute)',
      '1-4 weeks',
      '1-3 months',
      '3-6 months',
      '6-12 months',
      'More than 1 year (chronic)'
    ],
    required: true
  },
  {
    id: 'injury_cause',
    text: 'What caused the injury/condition?',
    inputType: 'select',
    options: [
      'Sports/Athletic activity',
      'Accident/Trauma',
      'Overuse/Repetitive strain',
      'Surgery',
      'Gradual onset (unknown cause)',
      'Work-related',
      'Other'
    ],
    required: true
  },
  {
    id: 'pain_level',
    text: 'Current pain level (0 = no pain, 10 = worst pain)',
    inputType: 'scale',
    options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'],
    required: true
  },
  {
    id: 'function_limitation',
    text: 'How much does this condition limit your daily activities?',
    inputType: 'select',
    options: [
      'Not at all',
      'Slightly - minor inconvenience',
      'Moderately - affects some activities',
      'Significantly - affects most activities',
      'Severely - unable to perform normal activities'
    ],
    required: true
  }
];

// ============================================================================
// Previous Treatments
// ============================================================================

const TREATMENT_HISTORY_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'previous_treatments',
    text: 'What treatments have you tried for this condition?',
    inputType: 'multiselect',
    options: [
      'None',
      'Rest/Ice/Compression',
      'Over-the-counter pain medications (NSAIDs)',
      'Prescription pain medications',
      'Physical therapy',
      'Chiropractic care',
      'Massage therapy',
      'Steroid injections',
      'PRP (Platelet-Rich Plasma)',
      'Stem cell therapy',
      'Surgery',
      'Other peptides',
      'Other'
    ],
    required: true
  },
  {
    id: 'treatment_effectiveness',
    text: 'How effective were previous treatments?',
    inputType: 'select',
    options: [
      'N/A - No previous treatment',
      'Not effective',
      'Slightly helpful',
      'Moderately helpful',
      'Very helpful but temporary',
      'Resolved but returned'
    ]
  },
  {
    id: 'current_medications',
    text: 'Are you currently taking any medications for this condition?',
    inputType: 'multiselect',
    options: [
      'Ibuprofen', 'Naproxen', 'Acetaminophen', 'Celecoxib',
      'Gabapentin', 'Pregabalin', 'Prednisone', 'Methylprednisolone',
      'Tramadol', 'Cyclobenzaprine', 'Meloxicam',
      'None',
    ],
    helpText: 'Select all that apply'
  },
  {
    id: 'nsaid_use',
    text: 'Are you currently using NSAIDs (ibuprofen, naproxen, etc.)?',
    inputType: 'boolean',
    required: true,
    helpText: 'Important: NSAIDs may affect peptide effectiveness'
  }
];

// ============================================================================
// Athlete Screening
// ============================================================================

const ATHLETE_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'athlete_status',
    text: 'Are you a competitive athlete?',
    inputType: 'select',
    options: [
      'No - recreational exercise only',
      'Amateur competitor',
      'College/University athlete',
      'Professional athlete',
      'Olympic/National team level'
    ],
    required: true
  },
  {
    id: 'drug_tested',
    text: 'Are you subject to drug testing (WADA/USADA/other)?',
    inputType: 'boolean',
    required: true,
    helpText: 'IMPORTANT: Many peptides are prohibited in competition'
  },
  {
    id: 'sport_type',
    text: 'What sport(s) do you participate in?',
    inputType: 'text',
    conditionalOn: {
      questionId: 'athlete_status',
      operator: 'notEquals',
      value: 'No - recreational exercise only'
    }
  },
  {
    id: 'next_competition',
    text: 'When is your next competition (if applicable)?',
    inputType: 'text',
    conditionalOn: {
      questionId: 'drug_tested',
      operator: 'equals',
      value: true
    },
    helpText: 'Important for clearance timing'
  },
  {
    id: 'wada_acknowledgment',
    text: 'I understand that BPC-157, TB-500, and many peptides are prohibited by WADA/USADA and most sports organizations.',
    inputType: 'boolean',
    conditionalOn: {
      questionId: 'drug_tested',
      operator: 'equals',
      value: true
    },
    required: true
  }
];

// ============================================================================
// Medical History
// ============================================================================

const MEDICAL_HISTORY_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'cancer_history',
    text: 'Have you ever been diagnosed with cancer?',
    inputType: 'boolean',
    required: true,
    helpText: 'Peptides may affect cell growth'
  },
  {
    id: 'cancer_details',
    text: 'If yes, please provide details:',
    inputType: 'text',
    conditionalOn: {
      questionId: 'cancer_history',
      operator: 'equals',
      value: true
    }
  },
  {
    id: 'autoimmune_disease',
    text: 'Do you have any autoimmune conditions?',
    inputType: 'boolean',
    required: true
  },
  {
    id: 'autoimmune_details',
    text: 'If yes, which condition(s)?',
    inputType: 'text',
    conditionalOn: {
      questionId: 'autoimmune_disease',
      operator: 'equals',
      value: true
    }
  },
  {
    id: 'bleeding_disorders',
    text: 'Do you have any bleeding disorders?',
    inputType: 'boolean',
    required: true
  },
  {
    id: 'liver_disease',
    text: 'Do you have liver disease or elevated liver enzymes?',
    inputType: 'boolean',
    required: true
  },
  {
    id: 'kidney_disease',
    text: 'Do you have kidney disease?',
    inputType: 'boolean',
    required: true
  },
  {
    id: 'pregnant_nursing',
    text: 'Are you pregnant or nursing? (If applicable)',
    inputType: 'select',
    options: ['No', 'Yes - pregnant', 'Yes - nursing', 'N/A'],
    required: true
  },
  {
    id: 'allergies',
    text: 'Do you have any drug allergies?',
    inputType: 'text',
    required: true
  },
  {
    id: 'all_medications',
    text: 'Select all current medications and supplements:',
    inputType: 'multiselect',
    options: [
      'Levothyroxine', 'Liothyronine',
      'Lisinopril', 'Amlodipine', 'Losartan', 'Metoprolol', 'Hydrochlorothiazide',
      'Metformin', 'Insulin',
      'Atorvastatin', 'Rosuvastatin', 'Simvastatin',
      'Sertraline', 'Fluoxetine', 'Escitalopram', 'Bupropion', 'Trazodone',
      'Ibuprofen', 'Naproxen', 'Acetaminophen', 'Gabapentin', 'Prednisone',
      'Omeprazole', 'Pantoprazole', 'Albuterol',
      'Testosterone', 'Estradiol', 'Progesterone',
      'Vitamin D', 'Vitamin B12', 'Iron supplement', 'Calcium', 'Magnesium', 'Multivitamin', 'Fish oil / Omega-3', 'Probiotics',
    ],
    required: true,
    helpText: 'Select all that apply, including prescriptions, OTC, and supplements.'
  }
];

// ============================================================================
// Injection Comfort
// ============================================================================

const INJECTION_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'injection_experience',
    text: 'Have you self-injected medications before?',
    inputType: 'select',
    options: [
      'No, this would be my first time',
      'Yes, occasionally',
      'Yes, regularly (insulin, HRT, etc.)',
      'Yes, experienced with peptides'
    ],
    required: true
  },
  {
    id: 'injection_comfort',
    text: 'How comfortable are you with self-injection?',
    inputType: 'scale',
    options: ['Very uncomfortable', 'Somewhat uncomfortable', 'Neutral', 'Comfortable', 'Very comfortable'],
    required: true
  },
  {
    id: 'injection_site_preference',
    text: 'Do you have a preference for injection site?',
    inputType: 'multiselect',
    options: [
      'No preference',
      'Abdomen',
      'Thigh',
      'Upper arm',
      'Near injury site (if applicable)'
    ]
  }
];

// ============================================================================
// Sections
// ============================================================================

const INJURY_SECTION: IntakeSection = {
  id: 'injury_assessment',
  title: 'Injury/Condition Assessment',
  description: 'Tell us about the condition you want to address.',
  icon: 'activity',
  questions: INJURY_QUESTIONS
};

const TREATMENT_SECTION: IntakeSection = {
  id: 'treatment_history',
  title: 'Previous Treatments',
  description: 'What have you tried so far?',
  icon: 'clipboard',
  questions: TREATMENT_HISTORY_QUESTIONS
};

const ATHLETE_SECTION: IntakeSection = {
  id: 'athlete_screening',
  title: 'Athletic Status',
  description: 'Important for WADA/USADA compliance.',
  icon: 'trophy',
  questions: ATHLETE_QUESTIONS
};

const MEDICAL_SECTION: IntakeSection = {
  id: 'medical_history',
  title: 'Medical History',
  description: 'Important health information for safe prescribing.',
  icon: 'heart',
  questions: MEDICAL_HISTORY_QUESTIONS
};

const INJECTION_SECTION: IntakeSection = {
  id: 'injection_comfort',
  title: 'Injection Comfort',
  description: 'Most peptides require subcutaneous injection.',
  icon: 'syringe',
  questions: INJECTION_QUESTIONS
};

const SHIPPING_BILLING_SECTION: IntakeSection = {
  id: 'shipping_billing_address',
  title: 'Shipping & Billing Address',
  description: 'Where should we ship your medications and send billing?',
  icon: 'package',
  questions: [
    {
      id: 'shipping_address_line1',
      text: 'Shipping Street Address',
      inputType: 'text',
      required: true,
    },
    {
      id: 'shipping_address_line2',
      text: 'Shipping Apt / Suite / Unit',
      inputType: 'text',
    },
    {
      id: 'shipping_city',
      text: 'Shipping City',
      inputType: 'text',
      required: true,
    },
    {
      id: 'shipping_state',
      text: 'Shipping State',
      inputType: 'select',
      options: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming', 'District of Columbia',
      ],
      required: true,
    },
    {
      id: 'shipping_postal_code',
      text: 'Shipping ZIP Code',
      inputType: 'text',
      required: true,
      validationRules: [
        { type: 'pattern', value: '^\\d{5}(-\\d{4})?$', message: 'Please enter a valid ZIP code' }
      ],
    },
    {
      id: 'billing_same_as_shipping',
      text: 'Is your billing address the same as your shipping address?',
      inputType: 'boolean',
      required: true,
    },
    {
      id: 'billing_address_line1',
      text: 'Billing Street Address',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_address_line2',
      text: 'Billing Apt / Suite / Unit',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_city',
      text: 'Billing City',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_state',
      text: 'Billing State',
      inputType: 'select',
      options: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming', 'District of Columbia',
      ],
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_postal_code',
      text: 'Billing ZIP Code',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
      validationRules: [
        { type: 'pattern', value: '^\\d{5}(-\\d{4})?$', message: 'Please enter a valid ZIP code' }
      ],
    },
  ]
};

const GOALS_SECTION: IntakeSection = {
  id: 'goals',
  title: 'Treatment Goals',
  description: 'What do you hope to achieve?',
  icon: 'target',
  questions: [
    {
      id: 'treatment_goals',
      text: 'What are your primary goals?',
      inputType: 'multiselect',
      options: [
        'Pain relief',
        'Faster healing',
        'Return to sport/activity',
        'Improve mobility/function',
        'Reduce inflammation',
        'Avoid surgery',
        'Post-surgical recovery',
        'Gut healing',
        'General recovery support'
      ],
      required: true
    },
    {
      id: 'timeline',
      text: 'What is your expected timeline?',
      inputType: 'select',
      options: [
        'As soon as possible',
        '2-4 weeks',
        '1-2 months',
        '3+ months',
        'No rush - gradual improvement'
      ]
    },
    {
      id: 'peptide_preference',
      text: 'Do you have a preference for which peptide?',
      inputType: 'select',
      options: [
        'No preference - provider recommendation',
        'BPC-157',
        'TB-500 (Thymosin Beta-4)',
        'BPC-157 + TB-500 combination',
        'Other (specify in notes)'
      ]
    },
    {
      id: 'additional_notes',
      text: 'Any additional information or questions?',
      inputType: 'text'
    }
  ]
};

// ============================================================================
// Complete Template
// ============================================================================

export const PEPTIDE_HEALING_TEMPLATE: IntakeTemplate = {
  id: 'peptide_healing_intake',
  templateType: 'peptide_healing',
  name: 'Peptide Therapy Intake (Healing/Recovery)',
  description: 'Intake for BPC-157, TB-500, and other healing peptides. Includes injury assessment, treatment history, and WADA/athletic screening.',
  estimatedTime: '8-12 minutes',
  sections: [
    INJURY_SECTION,
    TREATMENT_SECTION,
    ATHLETE_SECTION,
    MEDICAL_SECTION,
    INJECTION_SECTION,
    SHIPPING_BILLING_SECTION,
    GOALS_SECTION
  ]
};

/**
 * Check for athletic/WADA concerns
 */
export function checkWADAConcerns(responses: Record<string, boolean | string>): {
  isTestedAthlete: boolean;
  hasAcknowledged: boolean;
  warnings: string[];
} {
  const warnings: string[] = [];
  const isTestedAthlete = responses.drug_tested === true;
  const hasAcknowledged = responses.wada_acknowledgment === true;

  if (isTestedAthlete) {
    warnings.push('Patient is subject to drug testing');
    warnings.push('BPC-157 and TB-500 are prohibited substances under WADA');
    
    if (!hasAcknowledged) {
      warnings.push('Patient has NOT acknowledged WADA prohibition');
    }
  }

  const athleteStatus = responses.athlete_status as string;
  if (athleteStatus && !athleteStatus.includes('No - recreational')) {
    warnings.push(`Competitive athlete: ${athleteStatus}`);
  }

  return {
    isTestedAthlete,
    hasAcknowledged,
    warnings
  };
}

/**
 * Calculate injury severity score
 */
export function calculateInjurySeverity(responses: Record<string, string | number>): {
  severity: 'mild' | 'moderate' | 'severe';
  score: number;
  recommendation: string;
} {
  let score = 0;

  // Pain level (0-10)
  const painLevel = typeof responses.pain_level === 'number' 
    ? responses.pain_level 
    : parseInt(responses.pain_level as string) || 0;
  score += painLevel * 2;

  // Duration
  const duration = responses.injury_duration as string;
  if (duration?.includes('3-6 months')) score += 5;
  else if (duration?.includes('6-12 months')) score += 8;
  else if (duration?.includes('More than 1 year')) score += 10;
  else if (duration?.includes('1-3 months')) score += 3;

  // Function limitation
  const limitation = responses.function_limitation as string;
  if (limitation?.includes('Significantly')) score += 8;
  else if (limitation?.includes('Severely')) score += 12;
  else if (limitation?.includes('Moderately')) score += 5;

  let severity: 'mild' | 'moderate' | 'severe';
  let recommendation: string;

  if (score <= 10) {
    severity = 'mild';
    recommendation = 'Standard BPC-157 protocol may be appropriate';
  } else if (score <= 20) {
    severity = 'moderate';
    recommendation = 'Consider BPC-157 + TB-500 combination protocol';
  } else {
    severity = 'severe';
    recommendation = 'Comprehensive protocol recommended - consider combination therapy with longer duration';
  }

  return { severity, score, recommendation };
}

export default PEPTIDE_HEALING_TEMPLATE;
